import { Sidebar } from "primereact/sidebar";

interface MobileNavLeafEntry {
  label: string;
  route: string;
  visible?: boolean;
  openInNewTab?: boolean;
}

interface MobileNavGroupEntry {
  label: string;
  visible?: boolean;
  children: MobileNavLeafEntry[];
}

// Discriminated on "children" so a route is guaranteed wherever one is read, no `!` needed.
export type MobileNavEntry = MobileNavLeafEntry | MobileNavGroupEntry;

interface MobileNavDrawerProps {
  visible: boolean;
  onHide: () => void;
  entries: MobileNavEntry[];
  navigateTo: (route: string, openInNewTab?: boolean) => void;
}

/**
 * Left-side drawer used below the `xl` breakpoint in place of the desktop
 * Menubar. Renders the same nav entries as plain, always-expanded links: the
 * one entry with children (Champion Passport) is shown as a labelled group
 * rather than a collapsible submenu, since a drawer has room for both items
 * at once and that avoids re-implementing Menubar's own submenu toggle here.
 */
export const MobileNavDrawer = ({ visible, onHide, entries, navigateTo }: MobileNavDrawerProps) => {
  const handleNavigate = (route: string, openInNewTab?: boolean) => {
    onHide();
    navigateTo(route, openInNewTab);
  };

  return (
    <Sidebar
      visible={visible}
      onHide={onHide}
      position="left"
      dismissable
      closeOnEscape
      ariaCloseLabel="Close navigation menu"
      header="Menu"
      id="mobile-nav-drawer"
      data-testid="mobile-nav-drawer"
    >
      <nav aria-label="Mobile navigation">
        <ul className="flex flex-col gap-1 list-none m-0 p-0">
          {entries
            .filter(entry => entry.visible !== false)
            .map(entry =>
              "children" in entry ? (
                <li key={`group:${entry.label}`}>
                  <span className="block px-3 py-2 font-semibold">{entry.label}</span>
                  <ul className="flex flex-col gap-1 list-none m-0 p-0 pl-3">
                    {entry.children
                      .filter(child => child.visible !== false)
                      .map(child => (
                        <li key={child.route}>
                          <a
                            className="block px-3 py-2 min-h-11 cursor-pointer"
                            tabIndex={0}
                            onClick={() => handleNavigate(child.route, child.openInNewTab)}
                            onKeyDown={e => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleNavigate(child.route, child.openInNewTab);
                              }
                            }}
                          >
                            {child.label}
                          </a>
                        </li>
                      ))}
                  </ul>
                </li>
              ) : (
                <li key={entry.route}>
                  <a
                    className="block px-3 py-2 min-h-11 cursor-pointer"
                    tabIndex={0}
                    onClick={() => handleNavigate(entry.route, entry.openInNewTab)}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleNavigate(entry.route, entry.openInNewTab);
                      }
                    }}
                  >
                    {entry.label}
                  </a>
                </li>
              )
            )}
        </ul>
      </nav>
    </Sidebar>
  );
};
